import React from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'sonner';
import { useSWRConfig } from 'swr';

import { Box } from '@material-ui/core';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Chip from '@material-ui/core/Chip';
import Divider from '@material-ui/core/Divider';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import ArrowBackRoundedIcon from '@material-ui/icons/ArrowBackRounded';

import Button from 'creative-components/CustomButtons/Button';

import CampaignInfoCards from 'components/CampaignInfoCards/CampaignInfoCards';
import { useLoadingIndicatorContext } from 'components/LoadingIndicator/LoadingIndicatorProvider';
import AddPaymentMethodButton from 'components/PaymentMethods/AddPaymentMethodButton';
import PaymentMethodSelector from 'components/PaymentMethods/PaymentMethodSelector';

import { createCampaign, updateCampaign } from 'utils/api';
import {
  calculateCampaignDates,
  formatCents,
  getNumDaysAutoRenewalCampaignStartsIn,
  numberWithCommas,
  showAPIErrorAlert,
} from 'utils/lib';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  bubble: {
    maxWidth: '700px',
    margin: '30px auto 0',
  },
  section: {
    marginTop: '32px',
  },
  summaryList: {
    margin: '16px 0 0',
    padding: 0,
    listStyle: 'none',
    color: theme.palette.darkGray.main,
    '& > li': {
      marginBottom: '12px',
    },
  },
  paymentSection: {
    marginTop: '32px',
    maxWidth: '420px',
  },
  paymentControls: {
    marginTop: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  deleteDraftButton: {
    marginRight: '20px',
  },
  backButton: {
    marginRight: '20px',
  },
  sectionCards: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '24px',
    [theme.breakpoints.up('md')]: {
      gridTemplateColumns: '1fr 1fr',
    },
  },
  cardOffset: {
    backgroundColor:
    (theme.palette.offsetWhite && (theme.palette.offsetWhite.main || theme.palette.offsetWhite))
    || theme.palette.grey[50],
    borderRadius: 12,
    boxShadow: 'none',
    border: `1px solid ${theme.palette.grey[200]}`,
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    rowGap: 12,
    columnGap: 16,
    '& dt': {
      margin: 0,
      color: theme.palette.darkGray.main,
      fontWeight: 500,
    },
    '& dd': {
      margin: 0,
      textAlign: 'right',
    },
  },
  summaryChip: {
    marginLeft: 8,
    height: 24,
  },
}));

const CreateCampaignReview = ({
  draftCampaign,
  setDraftCampaign,
  confirmDraftDeletion,
  setStep,
  isAnnualBilling,
  handleSetAnnualBilling,
}) => {
  const classes = useStyles();
  const history = useHistory();
  const { mutate } = useSWRConfig();
  const { showLoadingIndicatorModal, hideLoadingIndicatorModal } = useLoadingIndicatorContext();

  const [paymentMethodId, setPaymentMethodId] = React.useState(draftCampaign?.stripe?.paymentMethodId ?? '');

  // React.useEffect(() => {
  //   setPaymentMethodId(draftCampaign?.stripe?.paymentMethodId ?? '');
  // }, [draftCampaign?._id, draftCampaign?.stripe?.paymentMethodId]);

  if (!draftCampaign) return null;

  const extraRecipientsCount = draftCampaign.extraMailOutRecipients?.length ?? 0;
  const estimatedApns = draftCampaign.estimatedNumberOfApns ?? 0;
  const { campaignStartDate, campaignEndDate } = calculateCampaignDates(draftCampaign.eventType);

  const campaignCosts = {
    ...draftCampaign.estimatedCosts,
    isAnnualBilling,
  };

  const onStartCampaign = async () => {
    if (!paymentMethodId) {
      toast.warning('Please provide a payment method!');
      return;
    }

    if (draftCampaign.autoRenewalForCampaign) {
      // Users can't start auto-renewing campaigns. They must wait for them to start.
      toast.warning(`This auto-renewal campaign will automatically start in ${getNumDaysAutoRenewalCampaignStartsIn(draftCampaign.createdAt)} day(s)`);
      return;
    }

    showLoadingIndicatorModal();

    try {
      await createCampaign(draftCampaign._id, paymentMethodId);

      mutate(`/campaigns/${draftCampaign._id}`);

      toast.success('Your campaign has been created and will start shortly! Give us some time to crunch the data and prepare everything for you.');
      history.push('/dashboard/campaigns');
    } catch (err) {
      console.error('error', err);
      showAPIErrorAlert(err);
    }

    hideLoadingIndicatorModal();
  };

  const handlePaymentMethodChange = async (event) => {
    const newPaymentMethodId = event.target.value;
    const previousPaymentMethodId = paymentMethodId;

    setPaymentMethodId(newPaymentMethodId);

    if (newPaymentMethodId === draftCampaign.stripe?.paymentMethodId) return;

    try {
      await updateCampaign(draftCampaign._id, { paymentMethodId: newPaymentMethodId });

      setDraftCampaign((prev) => ({
        ...prev,
        stripe: {
          ...(prev.stripe ?? {}),
          paymentMethodId: newPaymentMethodId,
        },
      }));
    } catch (err) {
      setPaymentMethodId(previousPaymentMethodId);
      console.error('error', err);
      showAPIErrorAlert(err);
    }
  };

  return (
    <div className={classes.root}>
      <CampaignInfoCards
        startDate={campaignStartDate}
        endDate={campaignEndDate}
        eventType={draftCampaign.eventType}
        campaignCosts={campaignCosts}
        valuesAreEstimates
        apnsTotalSelectedCount={estimatedApns}
        apnsFarmingStoppedCount={0}
        isCreating
        apnsTotalInAreaCount={undefined}
        setIsAnnualBilling={handleSetAnnualBilling}
      />

      <Box sx={{ mt: 4 }}>
        <Button
          simple
          color="dark"
          onClick={() => setStep(2)}
          className={classes.backButton}
        >
          <ArrowBackRoundedIcon />
          Back to Touch Review
        </Button>
      </Box>

      <div className={classes.sectionCards}>
        {/* Campaign Summary */}
        <Card className={classes.cardOffset}>
          <CardContent>
            <div className={classes.cardHeader}>
              <Typography variant="h6">Campaign Summary</Typography>
            </div>
            <Divider />

            <div style={{ marginTop: 16 }}>
              <dl className={classes.summaryGrid}>
                <dt>Campaign Type</dt>
                <dd>{draftCampaign.eventType.name}</dd>

                <dt>Estimated APNs</dt>
                <dd>{numberWithCommas(estimatedApns)}</dd>

                <dt>Extra Recipients</dt>
                <dd>{numberWithCommas(extraRecipientsCount)}</dd>

                <dt>Billing Preference</dt>
                <dd>
                  <Chip
                    size="small"
                    color={campaignCosts.isAnnualBilling ? 'primary' : 'default'}
                    label={campaignCosts.isAnnualBilling ? 'Annual (1 month free)' : 'Monthly'}
                    className={classes.summaryChip}
                  />
                </dd>

                {campaignCosts.isAnnualBilling && campaignCosts.annualDiscount ? (
                  <>
                    <dt>Annual Billing Discount</dt>
                    <dd>
                      {`Save ${formatCents(campaignCosts.annualDiscount)}`}
                    </dd>
                  </>
                ) : null}
              </dl>
            </div>
          </CardContent>
        </Card>

        {/* Billing & Payment */}
        <Card className={classes.cardOffset}>
          <CardContent>
            <div className={classes.cardHeader}>
              <Typography variant="h6">Billing &amp; Payment</Typography>
            </div>
            <Divider />

            <div className={classes.paymentControls}>
              <PaymentMethodSelector
                inputProps={{
                  value: paymentMethodId,
                  onChange: handlePaymentMethodChange,
                }}
              />
              <AddPaymentMethodButton center />
            </div>
          </CardContent>
        </Card>
      </div>

      <Box display="flex" flexDirection="row" justifyContent="flex-end" styles={{ marginTop: '48px' }}>
        <Button
          round
          color="primary"
          onClick={onStartCampaign}
        >
          Start Campaign
        </Button>
      </Box>
    </div>
  );
};

export default CreateCampaignReview;
